/* eslint-disable max-lines */
// ^^^ because all staff related analytics must be in one place
import React, {useEffect} from 'react';

import {sendDesktopAnalyticsPerSession} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {sendMobileAnalyticsPerSession} from '@optimaxdev/analytics/mobile/lib/sendAnalyticsEvent/index';
import {IntersectionOptions, InView} from 'react-intersection-observer';

import {GENERAL_EXPERIMENT_INDICATION} from 'constants/analytics/events/general';
import {flags, isUhcEnabled} from 'libs/rollout';
import {PlatformType} from 'types/types';

export type FeatureFlagsKeyType = keyof typeof flags | 'uhc';

type PropsType<T = FeatureFlagsKeyType> = {
    flag: T;
    children?: React.ReactNode;
    fallback?: React.ReactNode;
    cookies?: {[key: string]: string};
    inViewProps?: IntersectionOptions;
    isInteraction?: boolean;
    platform: PlatformType;
};

type FeaturesType = {
    [key: string]: (cookies?: {[key: string]: string}) => boolean;
};

const features: FeaturesType = {
    ...(Object.keys(flags) as (keyof typeof flags)[]).reduce<{
        [key: string]: () => boolean | string;
    }>((acc, key) => {
        const flag = flags[key];
        return {
            ...acc,
            // this will be needed in case when we will have variation flag
            [key]: () => ('isEnabled' in flag ? flag.isEnabled() : flag.getValue()),
        };
    }, {}),
    uhc: cookies => isUhcEnabled(cookies),
};

/**
 * Feature hook, accept name of the flag name,
 * @param {string} flag - feature name
 * @returns {boolean} Status of feature
 */
export const useFeature = (flag: FeatureFlagsKeyType) => {
    if (!features[flag]) return false;

    return features[flag]();
};

/**
 * useFeatureWithAnalytics hook, accepts name of the flag, sends GA event and returns flag's value
 * @param {string} flag - feature name
 * @param {boolean} isInteraction - Is interaction event
 * @param {PlatformType} platform - platform
 * @returns {boolean} Status of feature
 */
export const useFeatureWithAnalytics = (
    flag: FeatureFlagsKeyType,
    isInteraction = true,
    platform: PlatformType,
) => {
    useEffect(() => {
        if (features[flag]) {
            if (platform === 'desktop') {
                sendDesktopAnalyticsPerSession({
                    event: GENERAL_EXPERIMENT_INDICATION,
                    isInteraction,
                    label: {
                        expName: flag,
                        label: String(features[flag]()),
                    },
                });
            } else {
                sendMobileAnalyticsPerSession({
                    event: GENERAL_EXPERIMENT_INDICATION,
                    isInteraction,
                    label: {
                        expName: flag,
                        label: String(features[flag]()),
                    },
                });
            }
        }
    }, []);

    if (!features[flag]) return false;

    return features[flag]();
};

/**
 * Feature component, accept name of the flag name,
 * feature component as a child and callback if needed
 *
 * @returns {Node} Feature component
 */
export function Feature({
    children = null,
    cookies = {},
    fallback = null,
    flag,
}: PropsType): React.ReactElement {
    if (!features[flag]) return <>{fallback}</>;

    return <>{features[flag](cookies) ? children : fallback}</>;
}

/**
 * FeatureWithAnalytics component, accept name of the flag name,
 * feature component as a child and callback if needed
 * FeatureWithAnalytics is used when we want to send GA event on component mount
 *
 * @returns {ReactNode} Feature component
 */
export function FeatureWithAnalytics({
    children = null,
    cookies = {},
    fallback = null,
    flag,
    isInteraction = true,
    platform,
}: PropsType<FeatureFlagsKeyType>) {
    useEffect(() => {
        if (platform === 'desktop') {
            sendDesktopAnalyticsPerSession({
                event: GENERAL_EXPERIMENT_INDICATION,
                isInteraction,
                label: {
                    expName: flag,
                    label: String(features[flag]()),
                },
            });
        } else {
            sendMobileAnalyticsPerSession({
                event: GENERAL_EXPERIMENT_INDICATION,
                isInteraction,
                label: {
                    expName: flag,
                    label: String(features[flag]()),
                },
            });
        }
    }, []);

    if (!features[flag]) return <>{fallback}</>;

    return <>{features[flag](cookies) ? children : fallback}</>;
}

/**
 * FeatureWithAnalyticsOnView component, accept name of the flag name and all inView props,
 * feature component as a child and callback if needed
 * FeatureWithAnalyticsOnView is used when we want to send GA event on true view element as DOM element
 * FeatureWithAnalyticsOnView can apply default attributes like className and data/aria labels
 *
 * @returns {ReactNode} Feature component
 */
export function FeatureWithAnalyticsOnView({
    children = null,
    cookies = {},
    fallback = null,
    flag,
    inViewProps = {threshold: 1, triggerOnce: true},
    isInteraction = true,
    platform,
}: PropsType<FeatureFlagsKeyType>) {
    const onChange = (inView: boolean): void => {
        if (!inView) return;
        if (platform === 'desktop') {
            sendDesktopAnalyticsPerSession({
                event: GENERAL_EXPERIMENT_INDICATION,
                isInteraction,
                label: {
                    expName: flag,
                    label: String(features[flag]()),
                },
            });
        } else {
            sendMobileAnalyticsPerSession({
                event: GENERAL_EXPERIMENT_INDICATION,
                isInteraction,
                label: {
                    expName: flag,
                    label: String(features[flag]()),
                },
            });
        }
    };

    if (!features[flag]) return <>{fallback}</>;

    return (
        <InView onChange={onChange} {...inViewProps}>
            {features[flag](cookies) ? children : fallback}
        </InView>
    );
}
